import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { mergeRefs, useIsAutofilled } from '@pattern-library/base';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
const propTypes = {
  /**
   * DOM ID for the input element
   */
  id: PropTypes.string.isRequired,
  /**
   * Text label shown above the input element
   */
  label: PropTypes.string.isRequired,
  /**
   * Initial value for the input element or read-only label
   */
  defaultValue: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Flag to enable the input's error state
   */
  hasError: PropTypes.bool,
  /**
   * Flag to accessibly hide the input label
   */
  hideLabel: PropTypes.bool,
  /**
   * Content for the info popover. If not provided, the popover will not be rendered.
   * If useEmbeddedLabel is true, the popover will not be rendered.
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the message popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Additional classes for the textarea element
   */
  inputClass: PropTypes.string,
  /**
   * Flag to enable the input's disabled state
   */
  isDisabled: PropTypes.bool,
  /**
   * Flag to indicate whether this input is a required field.
   */
  isRequired: PropTypes.bool,
  /**
   * Flag to allow the user to resize the textarea.
   */
  isResizable: PropTypes.bool,
  /**
   * Flag to use a smaller format input width
   */
  isSmall: PropTypes.bool,
  /**
   * The maximum characters of the textarea. If provided, the component
   * will render a character count with the current and max number of characters.
   */
  maxCharacterCount: PropTypes.number,
  /**
   * The max height of the textarea. Once it reaches this height, the
   * textarea will scroll. Defaults to 500px.
   */
  maxHeight: PropTypes.string,
  /**
   * Appended to the text label.
   * Defaults to (optional) for non-required inputs, but can be overridden.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * The default height of the textarea in rows of text. Defaults to 4.
   */
  rows: PropTypes.number,
  /**
   * Append asterisk to label
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Text message shown below the input
   */
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};
const defaultProps = {
  defaultValue: '',
  hasError: false,
  hideLabel: false,
  errorMessage: null,
  errorLevel: 'error',
  infoPopoverContent: null,
  infoPopoverProps: {},
  inputClass: '',
  isDisabled: false,
  isRequired: false,
  isResizable: false,
  isSmall: false,
  maxHeight: '500px',
  optionalInputLabel: '(optional)',
  rows: 4,
  showRequiredAsterisk: false
};
function TextAreaInput(props) {
  const {
    defaultValue,
    errorLevel,
    errorMessage,
    hasError,
    hideLabel,
    id,
    infoPopoverContent,
    infoPopoverProps,
    inputClass,
    isDisabled,
    isRequired,
    isResizable,
    isSmall,
    label,
    maxCharacterCount,
    maxHeight,
    optionalInputLabel,
    rows,
    showRequiredAsterisk,
    subtext,
    ...inputProps
  } = props;
  const {
    'aria-describedby': ariaDescribedBy,
    onChange,
    ...restInputProps
  } = inputProps;
  const textareaRef = useRef();
  const isAutofilled = useIsAutofilled(textareaRef);
  const [uncontrolledValue, setUncontrolledValue] = useState('');
  const value = inputProps.value ?? uncontrolledValue;
  const hasValue = Boolean(value);
  const characterCount = value.length;
  const handleInputChange = event => {
    setUncontrolledValue(event.target.value);
    if (typeof onChange === 'function') {
      onChange(event);
    }
  };
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    characterCount: [`${id}-character-count`, Boolean(maxCharacterCount)],
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  let ariaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
  if (ariaDescribedBy) {
    ariaDescribedByIds = ariaDescribedByIds.concat(ariaDescribedBy);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classnames('asc-form-group asc-textarea-input', {
      'asc-form-group--with-value': hasValue,
      'asc-form-group--with-autofill': isAutofilled,
      'is-disabled': isDisabled,
      'is-resizable': isResizable,
      [`asc-form-input--${errorLevel}`]: hasError
    }),
    style: {
      '--asc-textarea-max-height': maxHeight
    }
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    hideLabel: hideLabel,
    inputId: id,
    isRequired: isRequired,
    label: label,
    optionalInputLabel: optionalInputLabel,
    showRequiredAsterisk: showRequiredAsterisk
  }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  }), /*#__PURE__*/React.createElement("div", {
    className: "asc-textarea-input__autosizer",
    "data-textarea-value": value
  }, /*#__PURE__*/React.createElement("textarea", _extends({
    className: classnames('asc-form-input', inputClass, {
      'asc-form-input--small': isSmall
    }),
    id: id,
    disabled: isDisabled,
    onChange: handleInputChange,
    required: isRequired,
    defaultValue: defaultValue ? defaultValue : undefined,
    "aria-describedby": ariaDescribedByIds.length ? ariaDescribedByIds.join(' ') : null,
    rows: rows
  }, restInputProps, {
    ref: mergeRefs([textareaRef, restInputProps?.ref])
  }))), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), maxCharacterCount && /*#__PURE__*/React.createElement("p", {
    id: ids.characterCount[0],
    className: "asc-form-subtext",
    "aria-live": "polite"
  }, characterCount, "/", maxCharacterCount, " characters"), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext"
  }, subtext));
}
TextAreaInput.propTypes = propTypes;
TextAreaInput.defaultProps = defaultProps;
export default TextAreaInput;